import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useCall } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import { IyenehContacts } from "@/components/IyenehContacts";
import { VoiceNote } from "@/components/VoiceNote";
import {
  PaperPlaneTilt, Phone, VideoCamera, Image as ImageIcon, Microphone, Stop,
  ArrowLeft, Check, Checks, X, ChatCircleText, DeviceMobile, Smiley,
  ArrowBendUpLeft, ShareFat, Clock, Timer, Images, DotsThreeVertical, PencilSimple, Trash,
} from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);
const EMOJIS = ["\uD83D\uDC4D", "\u2764\uFE0F", "\uD83D\uDE02", "\uD83D\uDE2E", "\uD83D\uDE22", "\uD83D\uDE4F"];
const DISAPPEAR = [{ v: 0, label: "Off" }, { v: 86400, label: "24 hours" }, { v: 604800, label: "7 days" }];
const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString();
};

export default function Text() {
  const [params, setParams] = useSearchParams();
  const withId = params.get("with") || "";
  const [tab, setTab] = useState("chats");
  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Messages</h1>
        <p className="mt-2 text-muted-foreground">Free chat with iYeneh members — photos, voice notes & video. Carrier SMS reaches any phone.</p>
      </div>
      <div className="flex gap-2" data-testid="text-tabs">
        <button data-testid="tab-chats" onClick={() => setTab("chats")}
          className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "chats" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>
          <ChatCircleText size={16} weight="fill" /> Chats <span className="rounded-full bg-emerald-100 px-1.5 text-[10px] text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">Free</span>
        </button>
        <button data-testid="tab-sms" onClick={() => setTab("sms")}
          className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "sms" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}>
          <DeviceMobile size={16} weight="fill" /> Carrier SMS
        </button>
      </div>
      {tab === "chats"
        ? <ChatsView withId={withId} setWith={(id) => setParams(id ? { with: id } : {})} />
        : <SmsView />}
    </div>
  );
}

function ChatsView({ withId, setWith }) {
  const qc = useQueryClient();
  const call = useCall();
  const { user } = useAuth();
  const myId = user?.id;

  const { data: threadsData } = useQuery({ queryKey: ["chat-threads"], queryFn: async () => (await api.get("/chat/threads")).data, refetchInterval: 8000 });
  const threads = threadsData?.threads || [];
  const { data: convo } = useQuery({
    queryKey: ["chat-messages", withId],
    queryFn: async () => (await api.get(`/chat/messages?with=${encodeURIComponent(withId)}`)).data,
    enabled: !!withId, refetchInterval: 4000,
  });
  const messages = convo?.messages || [];
  const other = convo?.other;
  const disappearing = convo?.disappearing_seconds || 0;
  const policy = convo?.policy || {};
  const allMedia = messages.flatMap((m) => (m.media || []).map((md) => ({ ...md, at: m.created_at })));
  const galleryMedia = allMedia.filter((m) => m.type === "image" || m.type === "video");
  const galleryDocs = allMedia.filter((m) => m.type !== "image" && m.type !== "video");
  const galleryLinks = messages.flatMap((m) => (m.text?.match(/https?:\/\/[^\s]+/g) || []));

  const [text, setText] = useState("");
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const [reactFor, setReactFor] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [showDisappear, setShowDisappear] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [menuFor, setMenuFor] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [galTab, setGalTab] = useState("media");
  const recRef = useRef(null); const chunksRef = useRef([]); const bottomRef = useRef(null);
  const typingSent = useRef(0); const typingTimer = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, peerTyping]);
  useEffect(() => { setReplyTo(null); setReactFor(null); setShowGallery(false); setMenuFor(null); setEditingId(null); }, [withId]);

  useEffect(() => {
    const onEvt = (e) => {
      const m = e.detail || {};
      if (["chat-message", "chat-read", "chat-reaction", "chat-edited", "chat-deleted"].includes(m.type)) qc.invalidateQueries({ queryKey: ["chat-messages", withId] });
      if (m.type === "chat-message") qc.invalidateQueries({ queryKey: ["chat-threads"] });
      if (m.type === "chat-settings" && m.with === withId) qc.invalidateQueries({ queryKey: ["chat-messages", withId] });
      if (m.type === "chat-typing" && m.with === withId) {
        setPeerTyping(!!m.typing);
        if (m.typing) { clearTimeout(typingTimer.current); typingTimer.current = setTimeout(() => setPeerTyping(false), 4000); }
      }
    };
    window.addEventListener("iyeneh-chat", onEvt);
    return () => window.removeEventListener("iyeneh-chat", onEvt);
  }, [withId, qc]);

  const notifyTyping = useCallback(() => {
    if (!withId) return;
    const now = Date.now();
    if (now - typingSent.current > 2500) { typingSent.current = now; api.post("/chat/typing", { to: withId, typing: true }).catch(() => {}); }
  }, [withId]);

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };
  const onPickFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setBusy(true);
    try { const m = await uploadFile(file); setPending((p) => [...p, m]); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const file = new File([new Blob(chunksRef.current, { type: "audio/webm" })], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        setBusy(true);
        try { const m = await uploadFile(file); setPending((p) => [...p, m]); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
      };
      recRef.current = mr; mr.start(); setRecording(true);
    } catch { toast.error("Microphone permission needed to record"); }
  };
  const stopRec = () => { try { recRef.current?.stop(); } catch {} setRecording(false); };

  const send = async () => {
    if (!withId || (!text.trim() && pending.length === 0)) return;
    setBusy(true);
    try {
      await api.post("/chat/send", { to: withId, text: text.trim(), media: pending, reply_to: replyTo?.id });
      setText(""); setPending([]); setReplyTo(null);
      api.post("/chat/typing", { to: withId, typing: false }).catch(() => {});
      qc.invalidateQueries({ queryKey: ["chat-messages", withId] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const react = async (mid, emoji) => {
    setReactFor(null);
    try { await api.post(`/chat/messages/${mid}/react`, { emoji }); qc.invalidateQueries({ queryKey: ["chat-messages", withId] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const ageSecs = (m) => (Date.now() - new Date(m.created_at).getTime()) / 1000;
  const canEdit = (m) => m.mine && !m.deleted && policy.edit_enabled && ageSecs(m) <= (policy.edit_window_secs ?? 0);
  const canDeleteAll = (m) => m.mine && !m.deleted && policy.delete_everyone_enabled && ageSecs(m) <= (policy.delete_everyone_window_secs ?? 0);
  const startEdit = (m) => { setMenuFor(null); setEditingId(m.id); setEditText(m.text || ""); };
  const saveEdit = async (m) => {
    const t = editText.trim(); if (!t) return;
    try { await api.post(`/chat/messages/${m.id}/edit`, { text: t }); setEditingId(null); qc.invalidateQueries({ queryKey: ["chat-messages", withId] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteForMe = async (m) => {
    setMenuFor(null);
    try { await api.post(`/chat/messages/${m.id}/delete-me`); qc.invalidateQueries({ queryKey: ["chat-messages", withId] }); qc.invalidateQueries({ queryKey: ["chat-threads"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteForEveryone = async (m) => {
    setMenuFor(null);
    try { await api.post(`/chat/messages/${m.id}/delete-everyone`); qc.invalidateQueries({ queryKey: ["chat-messages", withId] }); qc.invalidateQueries({ queryKey: ["chat-threads"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doForward = async (targetId) => {
    const m = forwardMsg; setForwardMsg(null); if (!m) return;
    try { await api.post("/chat/send", { to: targetId, text: m.text, media: m.media, forwarded: true }); toast.success("Forwarded"); qc.invalidateQueries({ queryKey: ["chat-threads"] }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setDisappear = async (secs) => {
    setShowDisappear(false);
    try { await api.post(`/chat/thread/${other.user_id}/settings`, { disappearing_seconds: secs }); toast.success(secs ? `Disappearing: ${DISAPPEAR.find((d) => d.v === secs).label}` : "Disappearing off"); qc.invalidateQueries({ queryKey: ["chat-messages", withId] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const renderMedia = (m, mine) => {
    const src = mediaUrl(m.url);
    if (m.type === "image") return <img key={src} src={src} alt={m.name || "photo"} className="mt-1 min-w-[80px] max-h-64 rounded-lg bg-black/5" />;
    if (m.type === "video") return <video key={src} src={src} controls className="mt-1 max-h-64 rounded-lg" />;
    if (m.type === "audio") return <VoiceNote key={src} src={m.url} mine={mine} />;
    return <a key={src} href={src} target="_blank" rel="noreferrer" className={`mt-1 block text-xs underline ${mine ? "text-white" : "text-primary"}`}>{m.name || "Attachment"}</a>;
  };

  if (withId && other) {
    const disLabel = DISAPPEAR.find((d) => d.v === disappearing)?.label;
    return (
      <div className="rounded-2xl border border-border bg-card" data-testid="chat-conversation">
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <button data-testid="chat-back" onClick={() => setWith("")} className="text-muted-foreground hover:text-foreground"><ArrowLeft size={20} /></button>
          <div className="relative">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-sm font-semibold text-primary">{(other.name || "?").charAt(0).toUpperCase()}</div>
            <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card ${other.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{other.name}</p>
            <p className="text-xs text-muted-foreground">{peerTyping ? "typing…" : other.online ? "Online" : "Offline"}{disappearing ? ` · ⏱ ${disLabel}` : ""}</p>
          </div>
          <div className="relative">
            <button data-testid="chat-disappear" onClick={() => setShowDisappear((s) => !s)} title="Disappearing messages" className={`rounded-full p-2 ${disappearing ? "text-primary" : "text-muted-foreground"} hover:bg-secondary`}><Timer size={18} weight={disappearing ? "fill" : "regular"} /></button>
            {showDisappear && (
              <div className="absolute right-0 z-20 mt-1 w-40 rounded-xl border border-border bg-card p-1 shadow-xl" data-testid="disappear-menu">
                {DISAPPEAR.map((d) => (
                  <button key={d.v} data-testid={`disappear-${d.v}`} onClick={() => setDisappear(d.v)} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-secondary ${disappearing === d.v ? "font-semibold text-primary" : ""}`}><Clock size={14} /> {d.label}</button>
                ))}
              </div>
            )}
          </div>
          <button data-testid="chat-gallery" onClick={() => { setGalTab("media"); setShowGallery(true); }} title="Media, links & docs" className="rounded-full p-2 text-muted-foreground hover:bg-secondary"><Images size={18} /></button>
          <button data-testid="chat-audio-call" onClick={() => call?.startCall?.(other.user_id, false)} className="rounded-full bg-primary/10 p-2 text-primary hover:scale-110"><Phone size={18} weight="fill" /></button>
          <button data-testid="chat-video-call" onClick={() => call?.startCall?.(other.user_id, true)} className="rounded-full bg-primary/10 p-2 text-primary hover:scale-110"><VideoCamera size={18} weight="fill" /></button>
        </div>

        <div className="h-[52vh] space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
          {messages.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">Say hello 👋 — messages here are free.</p>}
          {messages.map((m) => {
            const rx = Object.entries(m.reactions || {});
            return (
              <div key={m.id} className={`group flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className="relative max-w-[80%]">
                  <div className={`rounded-2xl px-3 py-2 text-sm ${m.mine ? "bg-primary text-primary-foreground" : "border border-border bg-secondary"}`}>
                    {m.forwarded && <p className={`mb-0.5 text-[10px] italic ${m.mine ? "text-white/70" : "text-muted-foreground"}`}>↪ Forwarded</p>}
                    {m.reply && (
                      <div className={`mb-1 rounded-lg border-l-2 px-2 py-1 text-xs ${m.mine ? "border-white/50 bg-white/10" : "border-primary/50 bg-background"}`}>
                        <span className="font-semibold">{m.reply.from_name}</span>
                        <p className="truncate opacity-80">{m.reply.text || (m.reply.media_type ? `📎 ${m.reply.media_type}` : "")}</p>
                      </div>
                    )}
                    {m.deleted
                      ? <p className="whitespace-pre-wrap break-words italic opacity-70">{m.text}</p>
                      : editingId === m.id
                        ? (
                          <div className="flex items-center gap-1">
                            <input data-testid={`edit-input-${m.id}`} value={editText} autoFocus
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(m); } if (e.key === "Escape") setEditingId(null); }}
                              className="min-w-0 flex-1 rounded-lg bg-white/20 px-2 py-1 text-sm text-inherit outline-none" />
                            <button data-testid={`edit-save-${m.id}`} onClick={() => saveEdit(m)} className="rounded-full bg-white/20 p-1"><Check size={14} weight="bold" /></button>
                            <button data-testid={`edit-cancel-${m.id}`} onClick={() => setEditingId(null)} className="rounded-full bg-white/20 p-1"><X size={14} /></button>
                          </div>
                        )
                        : (m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>)}
                    {!m.deleted && (m.media || []).map((md) => renderMedia(md, m.mine))}
                    <p className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${m.mine ? "text-white/70" : "text-muted-foreground"}`}>
                      {m.edited && !m.deleted && <span className="opacity-70">Edited</span>}
                      {relTime(m.created_at)}
                      {m.mine && !m.deleted && (m.read ? <Checks size={14} weight="bold" className="text-emerald-300" /> : <Check size={13} weight="bold" />)}
                    </p>
                  </div>

                  {rx.length > 0 && (
                    <div className={`mt-1 flex flex-wrap gap-1 ${m.mine ? "justify-end" : ""}`}>
                      {rx.map(([emo, users]) => (
                        <button key={emo} data-testid={`reaction-${m.id}-${emo}`} onClick={() => react(m.id, emo)}
                          className={`rounded-full border px-1.5 py-0.5 text-[11px] ${users.includes(myId) ? "border-primary bg-primary/10" : "border-border bg-card"}`}>{emo} {users.length}</button>
                      ))}
                    </div>
                  )}

                  <div className={`absolute top-0 ${m.mine ? "left-0 -translate-x-full pr-1" : "right-0 translate-x-full pl-1"} hidden items-center gap-0.5 group-hover:flex`}>
                    <button data-testid={`react-btn-${m.id}`} onClick={() => setReactFor(reactFor === m.id ? null : m.id)} className="rounded-full bg-card p-1.5 text-muted-foreground shadow hover:text-primary"><Smiley size={15} /></button>
                    <button data-testid={`reply-btn-${m.id}`} onClick={() => setReplyTo({ id: m.id, from_name: m.mine ? "You" : other.name, text: m.text, media_type: (m.media || [])[0]?.type })} className="rounded-full bg-card p-1.5 text-muted-foreground shadow hover:text-primary"><ArrowBendUpLeft size={15} /></button>
                    <button data-testid={`forward-btn-${m.id}`} onClick={() => setForwardMsg(m)} className="rounded-full bg-card p-1.5 text-muted-foreground shadow hover:text-primary"><ShareFat size={15} /></button>
                    {!m.deleted && <button data-testid={`menu-btn-${m.id}`} onClick={() => setMenuFor(menuFor === m.id ? null : m.id)} className="rounded-full bg-card p-1.5 text-muted-foreground shadow hover:text-primary"><DotsThreeVertical size={15} weight="bold" /></button>}
                  </div>
                  {menuFor === m.id && (
                    <div className={`absolute z-30 mt-1 w-48 rounded-xl border border-border bg-card p-1 text-foreground shadow-xl ${m.mine ? "right-0" : "left-0"}`} data-testid={`msg-menu-${m.id}`}>
                      {canEdit(m) && <button data-testid={`edit-msg-${m.id}`} onClick={() => startEdit(m)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-secondary"><PencilSimple size={14} /> Edit message</button>}
                      <button data-testid={`delete-me-${m.id}`} onClick={() => deleteForMe(m)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-secondary"><Trash size={14} /> Delete for me</button>
                      {canDeleteAll(m) && <button data-testid={`delete-all-${m.id}`} onClick={() => deleteForEveryone(m)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-destructive hover:bg-secondary"><Trash size={14} weight="fill" /> Delete for everyone</button>}
                    </div>
                  )}
                  {reactFor === m.id && (
                    <div className={`absolute z-20 mt-1 flex gap-1 rounded-full border border-border bg-card px-2 py-1 shadow-xl ${m.mine ? "right-0" : "left-0"}`} data-testid={`emoji-picker-${m.id}`}>
                      {EMOJIS.map((e) => <button key={e} onClick={() => react(m.id, e)} className="text-lg transition-transform hover:scale-125">{e}</button>)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {peerTyping && <div className="flex justify-start"><div className="rounded-2xl border border-border bg-secondary px-3 py-2 text-xs text-muted-foreground">typing…</div></div>}
          <div ref={bottomRef} />
        </div>

        {replyTo && (
          <div className="flex items-center gap-2 border-t border-border px-4 py-2 text-xs" data-testid="reply-preview">
            <ArrowBendUpLeft size={16} className="text-primary" />
            <div className="min-w-0 flex-1"><span className="font-semibold">{replyTo.from_name}</span><p className="truncate text-muted-foreground">{replyTo.text || replyTo.media_type}</p></div>
            <button onClick={() => setReplyTo(null)}><X size={14} /></button>
          </div>
        )}
        {pending.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-border px-4 py-2" data-testid="chat-attachments">
            {pending.map((m, i) => (
              <div key={i} className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs">
                {m.type === "image" ? "🖼" : m.type === "video" ? "🎥" : m.type === "audio" ? "🎤" : "📎"} {m.name?.slice(0, 16) || m.type}
                <button onClick={() => setPending((p) => p.filter((_, j) => j !== i))}><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 border-t border-border px-3 py-3">
          <label data-testid="chat-attach-image" className="cursor-pointer rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground" title="Photo or video">
            <ImageIcon size={20} /><input type="file" accept="image/*,video/*" className="hidden" onChange={onPickFile} />
          </label>
          {recording
            ? <button data-testid="chat-stop-rec" onClick={stopRec} className="rounded-full bg-destructive p-2 text-white animate-pulse" title="Stop"><Stop size={20} weight="fill" /></button>
            : <button data-testid="chat-record" onClick={startRec} className="rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground" title="Voice note"><Microphone size={20} /></button>}
          <input data-testid="chat-input" value={text}
            onChange={(e) => { setText(e.target.value); notifyTyping(); }}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            placeholder={recording ? "Recording voice…" : "Message"}
            className="min-w-0 flex-1 rounded-full border border-input bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
          <button data-testid="chat-send" onClick={send} disabled={busy || (!text.trim() && pending.length === 0)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"><PaperPlaneTilt size={18} weight="fill" /></button>
        </div>

        {showGallery && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowGallery(false)}>
            <div className="flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl border border-border bg-card" onClick={(e) => e.stopPropagation()} data-testid="chat-gallery-modal">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <h3 className="font-display text-lg font-semibold">Media, links & docs</h3>
                <button data-testid="gallery-close" onClick={() => setShowGallery(false)}><X size={18} /></button>
              </div>
              <div className="flex gap-2 border-b border-border px-4 py-2">
                {[["media", `Media (${galleryMedia.length})`], ["docs", `Docs (${galleryDocs.length})`], ["links", `Links (${galleryLinks.length})`]].map(([v, l]) => (
                  <button key={v} data-testid={`gallery-tab-${v}`} onClick={() => setGalTab(v)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${galTab === v ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {galTab === "media" && (galleryMedia.length === 0
                  ? <p className="py-10 text-center text-sm text-muted-foreground">No photos or videos yet.</p>
                  : <div className="grid grid-cols-3 gap-2">{galleryMedia.map((m, i) => m.type === "image"
                      ? <a key={i} href={mediaUrl(m.url)} target="_blank" rel="noreferrer"><img src={mediaUrl(m.url)} alt={m.name || "photo"} className="aspect-square w-full rounded-lg object-cover" /></a>
                      : <video key={i} src={mediaUrl(m.url)} className="aspect-square w-full rounded-lg object-cover" />)}</div>)}
                {galTab === "docs" && (galleryDocs.length === 0
                  ? <p className="py-10 text-center text-sm text-muted-foreground">No files or voice notes yet.</p>
                  : <div className="space-y-2">{galleryDocs.map((m, i) => (
                      <a key={i} href={mediaUrl(m.url)} target="_blank" rel="noreferrer" data-testid={`gallery-doc-${i}`} className="flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm hover:bg-secondary">
                        <span>{m.type === "audio" ? "🎤" : "📎"}</span><span className="truncate">{m.name || (m.type === "audio" ? "Voice note" : "Attachment")}</span>
                      </a>))}</div>)}
                {galTab === "links" && (galleryLinks.length === 0
                  ? <p className="py-10 text-center text-sm text-muted-foreground">No links shared yet.</p>
                  : <div className="space-y-2">{galleryLinks.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`gallery-link-${i}`} className="block truncate rounded-xl border border-border px-3 py-2 text-sm text-primary underline hover:bg-secondary">{u}</a>))}</div>)}
              </div>
            </div>
          </div>
        )}

        {forwardMsg && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setForwardMsg(null)}>
            <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-4" onClick={(e) => e.stopPropagation()} data-testid="forward-modal">
              <div className="mb-3 flex items-center justify-between"><h3 className="font-display text-lg font-semibold">Forward to…</h3><button onClick={() => setForwardMsg(null)}><X size={18} /></button></div>
              {threads.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No other chats to forward to yet.</p>}
              <div className="max-h-72 space-y-1 overflow-y-auto">
                {threads.filter((t) => t.user_id !== withId).map((t) => (
                  <button key={t.user_id} data-testid={`forward-to-${t.user_id}`} onClick={() => doForward(t.user_id)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-secondary">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-sm font-semibold text-primary">{(t.name || "?").charAt(0).toUpperCase()}</div>
                    <span className="truncate text-sm font-medium">{t.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-2" data-testid="chat-threads">
        {threads.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No chats yet. Find a member below to start a free conversation.</p>}
        {threads.map((t) => (
          <button key={t.user_id} data-testid={`thread-${t.user_id}`} onClick={() => setWith(t.user_id)} className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left hover:bg-secondary">
            <div className="relative">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent text-sm font-semibold text-primary">{(t.name || "?").charAt(0).toUpperCase()}</div>
              <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card ${t.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2"><p className="truncate text-sm font-semibold">{t.name}</p><span className="shrink-0 text-[10px] text-muted-foreground">{relTime(t.last_at)}</span></div>
              <p className="truncate text-xs text-muted-foreground">{t.last_mine ? "You: " : ""}{t.last_text}</p>
            </div>
            {t.unread > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">{t.unread}</span>}
          </button>
        ))}
      </div>
      <IyenehContacts title="Start a new chat" smsEnabled />
    </div>
  );
}

function SmsView() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sms-inbox"], queryFn: async () => (await api.get("/sms/inbox")).data, refetchInterval: 15000 });
  const messages = data?.messages || [];
  const [to, setTo] = useState(""); const [text, setText] = useState(""); const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.trim() || !text.trim()) { toast.error("Enter a number and a message"); return; }
    setBusy(true);
    try { await api.post("/sms/send", { to: to.trim(), text }); toast.success("Message sent"); setText(""); qc.invalidateQueries({ queryKey: ["sms-inbox"] }); qc.invalidateQueries({ queryKey: ["wallet"] }); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">New SMS · to any phone (paid)</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input data-testid="sms-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+12025551234" className="rounded-xl border border-input bg-card px-3 py-2.5 font-mono-metric text-sm outline-none focus:border-primary sm:w-48" />
          <input data-testid="sms-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" onKeyDown={(e) => e.key === "Enter" && send()} className="min-w-0 flex-1 rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary" />
          <button data-testid="sms-send" onClick={send} disabled={busy} className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"><PaperPlaneTilt size={16} weight="bold" /> {busy ? "Sending…" : "Send"}</button>
        </div>
      </div>
      <div className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Conversation history</h2>
        {messages.length === 0 && <div className="rounded-xl border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">No SMS yet.</div>}
        {messages.map((m) => (
          <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.direction === "outbound" ? "bg-primary text-primary-foreground" : "border border-border bg-card"}`}>
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-70">{m.direction === "outbound" ? `To ${m.to}` : `From ${m.from_number}`}</p>
              <p className="whitespace-pre-wrap">{m.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
